import {
    Button, ClickEvent, DataSourceMode, ForcedCase, Label, Layout, LookupModelSelectionEvent, Panel, SlideoutDecorator, Snackbar, Table,
    TableAction, TableContentsChangedEvent, TableRow, TableRowDisplayEvent, TableRowExpansionEvent
} from "@mcleod/components";
import { Api, ModelRow } from "@mcleod/core";
import { RecurringSchedule } from "./RecurringSchedule";
import { RecurringScheduleTable } from "./RecurringScheduleTable";
import { AutogenLayoutRecurringOrderList } from "./autogen/AutogenLayoutRecurringOrderList";

export class RecurringOrderList extends AutogenLayoutRecurringOrderList {
    private transitionProps = { speed: 100, paddingTop: 0 };

    override onLoad() {
        this.citystateStopOriginCityId.dataSourceModeChanged(DataSourceMode.SEARCH);
        this.citystateStopOriginCityId.textCity.forcedCase = ForcedCase.UPPER;
        this.citystateStopDestCityId.dataSourceModeChanged(DataSourceMode.SEARCH);
        this.citystateStopDestCityId.textCity.forcedCase = ForcedCase.UPPER;
        this.panelOrderListContainer.rows[0].style.height = "100%"; // pretty nasty hack to make the vertical scroll bar behave
        this.addLayoutLoadListener(() => {
            this.textboxId.focus();
        })

        this.textboxOriginLocation.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => 
            this.searchStopSelection("origin", this.panelShipperLocation, event));
        this.textboxDestLocation.addLookupModelSelectionListener(event => 
            this.searchStopSelection("consignee", this.panelConsigneeLocation, event)
        );

        this.textboxStopOriginLocationId.addLookupModelSelectionListener((event: LookupModelSelectionEvent) =>
            this.searchStopPopulateSelection("origin", this.panelShipperLocation, event));
        this.textboxStopDestLocationId.addLookupModelSelectionListener(event =>
            this.searchStopPopulateSelection("consignee", this.panelConsigneeLocation, event)
        );
    }

    tableRecurringOrdersOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow()
        if (tableRow.expanded) {
            tableRow.getExpansionComponentLoaded().then((expandComponent) => {
                const layoutRecScheduleTable: RecurringScheduleTable = (expandComponent as Panel).findComponentById("layoutRecScheduleTable") as RecurringScheduleTable;
                const id = tableRow.data.get("id");
                layoutRecScheduleTable.mainDataSource.search({ recurring_order_id: id });
            });
        }
    }

    async onRowExpand(event: TableRowExpansionEvent) {
        if (event.isExpanding) {
            const tableRow: TableRow = event.target as TableRow;
            const modelRow: ModelRow = tableRow.data
            const stopData = (await Api.search("lme/dispatch/stop-rec-order", { order_id: modelRow.get("id") })).data.map((row: any) => new ModelRow('lme/dispatch/recur-sched-stop', true, row));
            const expandPanel = event.expandComponentParent as Panel;
            const layoutRecScheduleTable: RecurringScheduleTable = expandPanel.findComponentById("layoutRecScheduleTable") as RecurringScheduleTable;
            const id = tableRow.data.get("id");
            layoutRecScheduleTable.addLayoutLoadListener(() => {
                layoutRecScheduleTable.tableRecurringSchedule.persistChangesImmediately = true;
                layoutRecScheduleTable.tableRecurringSchedule.addContentsChangedListener((event: TableContentsChangedEvent) => this.tableRecurringScheduleOnContentsChanged(event, tableRow));
                layoutRecScheduleTable.mainDataSource.search({ recurring_order_id: id });
                layoutRecScheduleTable.initialize(modelRow, stopData);
            });
            this.tableRecurringOrders.setRowsExpanded(false, (row: TableRow) => row.index !== tableRow.index);
        }
    }

    tableRecurringScheduleOnContentsChanged(event: TableContentsChangedEvent, tableRow: TableRow) {
        const scheduleTable = event.target as Table;
        if (TableAction.DELETE == event.getAction() && scheduleTable.rows.length == 0 && tableRow.data.get("scheduled") == "Y") {
            Snackbar.showWarningSnackbar("The recurring order must have at least one schedule assignment to be scheduled. Schedule has been turned off.");
            tableRow.data.set("scheduled", "N");
            tableRow.data.post();
        }
    }

    buttonSchedulerOnClick(event: ClickEvent) {
        const label = event.target as Label;
        this.showScheduleSlideout(label);
    }


    async showScheduleSlideout(label: Label) {
        let recurringScheduleLayout: RecurringSchedule;
        const tableRow = TableRow.getContainingTableRow(label);
        const modelRow = tableRow.data;
        const stopData = (await Api.search("lme/dispatch/stop-rec-order", { order_id: modelRow.get("id") })).data.map((row: any) => new ModelRow('lme/dispatch/recur-sched-stop', true, row));
        const erd = new SlideoutDecorator({
            title: `Recurring Order Schedule Assignments`,
            layout: recurringScheduleLayout = Layout.getLayout("lme/dispatch/RecurringSchedule") as RecurringSchedule,
            layoutLoadListeners: async (event) => {
                recurringScheduleLayout.initialize(DataSourceMode.ADD, modelRow, stopData);
            },
            width: '60%',
            fillVerticalSpace: true,
            overlayProps: { closeOnClickOff: false, greyedBackground: true },
            onClose: (cancelled: boolean) => {
                recurringScheduleLayout.slideOut();
            },
            addlComponents: new Button({
                id: "recSchedSaveButton",
                caption: "Save and Close",
                backgroundColor: "primary",
                color: "primary.reverse",
                minWidth: 128,
                borderWidth: 0,
                rowBreak: false,
                onClick: async (event: ClickEvent) => {
                    const updatedRow = await recurringScheduleLayout.onSave();
                    if (updatedRow) {
                        tableRow.data.set("frequency", updatedRow.data["frequency"]);
                        tableRow.data.set("occurrence", updatedRow.data["occurrence"]);
                        const expandPanel = await tableRow.setExpandedAsync(true) as Panel
                        const layoutRecScheduleTable: RecurringScheduleTable = expandPanel.findComponentById("layoutRecScheduleTable") as RecurringScheduleTable;
                        layoutRecScheduleTable.mainDataSource.search({ recurring_order_id: tableRow.data.get("id") });
                    }
                },
            })
        });
    }

    searchStopPopulateSelection(stopType: "origin" | "consignee", targetPanel: Panel, event: LookupModelSelectionEvent) {
        if (event.hasSelection()) {
            const selection = event.selection;
            this.sourceRecurringOrdersList.searchRow.setValues({
                [`stop_${stopType}.address`]: selection.getString("address1", null),
                [`stop_${stopType}.address2`]: selection.getString("address2", null),
                [`stop_${stopType}.city_id`]: selection.getString("city_id", null),
                [`stop_${stopType}.city_name`]: selection.getString("city_name", null),
                [`stop_${stopType}.location_id`]: selection.getString("id", null),
                [`stop_${stopType}.location_name`]: selection.getString("name", null),
                [`stop_${stopType}.state`]: selection.getString("state", null),
                [`stop_${stopType}.zip_code`]: selection.getString("zip_code", null),
            });
        }
        this.searchStopSelection(stopType, targetPanel, event);
    }

    searchStopSelection(stopType: "origin" | "consignee", targetPanel: Panel, event: LookupModelSelectionEvent) {
        if (event.hasSelection()) {
            const selection = event.selection;
            if (!selection.isNull("state")) {
                this.sourceRecurringOrdersList.searchRow.setLookupModelData(`stop_${stopType}.state`, { "id": selection.getString("state", null) });
            }
        }
        targetPanel.displayComponentData(this.sourceRecurringOrdersList.searchRow, null, 0);
    }
}
